import { createEmptyLocation, Location } from '@/model/Location';
import { createEmptyTopic, Topic } from '@/model/Topic';
import { Dictionary } from 'lodash';
import groupBy from 'lodash.groupby';
import uniqBy from 'lodash.uniqby';
import {
    EventFlow,
    EventFlowItem,
    EventFlowLocation,
    EventFlowTopic,
} from '@/model/event-flow/EventFlow';
import { isDateSameOrBefore, isDateTimeSameOrAfter } from '@/lib/date-util';
import { addMinutes, differenceInMinutes } from 'date-fns';

export function isDateTimeSameOrBeforeStart(
    dateTime: Date,
    startDateTime: Date,
): boolean {
    return isDateSameOrBefore(dateTime, startDateTime);
}

export function isDateTimeSameOrAfterEnd(
    dateTime: Date,
    endDateTime: Date,
): boolean {
    return isDateTimeSameOrAfter(dateTime, endDateTime);
}

export function isStartMovementOutOfBound(
    offsetY: number,
    startTime: Date,
    startDateTime: Date,
): boolean {
    return offsetY < 0 && isDateTimeSameOrBeforeStart(startTime, startDateTime);
}

export function isEndMovementOutOfBound(
    offsetY: number,
    endTime: Date,
    endDateTime: Date,
): boolean {
    return offsetY >= 0 && isDateTimeSameOrAfterEnd(endTime, endDateTime);
}

export function calculateNumberOfMovedSteps(
    offsetY: number,
    rowHeight: number,
): number {
    return Math.round(offsetY / rowHeight);
}

export function addOrSubtractStepsFromDate(
    dateTime: Date,
    movedSteps: number,
    step: number,
): Date {
    return addMinutes(dateTime, movedSteps * step);
}

export function calculatePos(
    timeValue: Date,
    startDateTime: Date,
    lastPos: number,
    step: number,
): number {
    if (timeValue <= startDateTime) {
        return 1;
    }

    const durationFromTopInMinutes = differenceInMinutes(
        timeValue,
        startDateTime,
    );

    const calculatedPos = Math.round(durationFromTopInMinutes / step) + 1;

    return calculatedPos > lastPos ? lastPos : calculatedPos;
}

export function setFlow(eventFlowItems: EventFlowItem[]): EventFlow {
    eventFlowItems.forEach((eventFlowItem) => {
        if (!eventFlowItem?.location) {
            eventFlowItem.location = createEmptyLocation();
        }

        if (!eventFlowItem?.topic) {
            eventFlowItem.topic = createEmptyTopic();
        }
    });

    const itemsGroupByLocation: Dictionary<EventFlowItem[]> = groupBy(
        eventFlowItems,
        (eventFlowItem) => eventFlowItem?.location?.id,
    );

    const locations: Location[] = uniqBy(
        eventFlowItems.map((eventFlowItem) => {
            return eventFlowItem.location ?? createEmptyLocation();
        }),
        (location) => location?.id,
    );

    const eventFlowLocations = locations
        .map((location) => {
            const itemsByLocation: EventFlowItem[] =
                itemsGroupByLocation[location.id ?? 0];

            const itemsGroupByTopic: Dictionary<EventFlowItem[]> = groupBy(
                itemsByLocation,
                (eventFlowItem) => eventFlowItem?.topic?.id,
            );

            const topics: Topic[] = uniqBy(
                itemsByLocation.map((eventFlowItem) => {
                    return eventFlowItem.topic ?? createEmptyTopic();
                }),
                (topic) => topic?.id,
            );

            const eventFlowTopics: EventFlowTopic[] = topics
                .map((topic) => {
                    return {
                        ...topic,
                        items: itemsGroupByTopic[topic.id ?? 0],
                    } as EventFlowTopic;
                })
                .sort((a, b) => {
                    if (!a?.order || !b?.order) {
                        return 1;
                    }

                    return a.order > b.order ? 1 : -1;
                });

            return {
                ...location,
                eventFlowTopics,
            } as EventFlowLocation;
        })
        .sort((a, b) => {
            if (!a?.order || !b?.order) {
                return 1;
            }

            return a.order > b.order ? 1 : -1;
        });

    let index = 0;

    eventFlowLocations.forEach((eventFlowLocation) => {
        eventFlowLocation.startIndex = index;

        eventFlowLocation.eventFlowTopics.forEach((eventFlowTopic) => {
            eventFlowTopic.startIndex = index;
            eventFlowTopic.endIndex = eventFlowTopic.items.length + index;

            eventFlowTopic.items
                .sort((a, b) => {
                    if (a.startedAt.getTime() === b.startedAt.getTime()) {
                        return a.endedAt > b.endedAt ? 1 : -1;
                    }

                    return a.startedAt > b.startedAt ? 1 : -1;
                })
                .map((eventFlowItem) => {
                    eventFlowItem.index = index;
                    index += 1;

                    return eventFlowItem;
                });

            eventFlowTopic.borderIndex = index;
            index += 1;
        });

        eventFlowLocation.endIndex = index;
        eventFlowLocation.borderIndex = index;
        index += 1;
    });

    eventFlowLocations.forEach((eventFlowLocation) => {
        if (eventFlowLocation.id === 0) {
            eventFlowLocation.id = null;
        }

        eventFlowLocation.eventFlowTopics.forEach((eventFlowTopic) => {
            if (eventFlowTopic.id === 0) {
                eventFlowTopic.id = null;
            }

            eventFlowTopic.items.forEach((eventFlowItem) => {
                if (eventFlowItem?.location?.id === 0) {
                    eventFlowItem.location = undefined;
                }

                if (eventFlowItem?.topic?.id === 0) {
                    eventFlowItem.topic = undefined;
                }
            });
        });
    });

    return {
        lastIndex: index,
        eventFlowLocations,
    };
}

export function getEnumValueByEnumKey<T>(
    myEnum: Record<string, T>,
    enumKey: T,
): unknown | null {
    const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumKey);
    return keys.length > 0 ? keys[0] : null;
}

export function getEnumKeyByEnumValue<T>(
    myEnum: Record<string, unknown>,
    enumValue: unknown,
): keyof T | null {
    const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
    return keys.length > 0 ? (keys[0] as keyof T) : null;
}

export function setStartDateTime(startDateTime: Date, date: Date): Date {
    startDateTime.setFullYear(date.getFullYear());
    startDateTime.setDate(date.getDate());
    startDateTime.setMonth(date.getMonth());

    return startDateTime;
}

export function setEndDateTime(endDateTime: Date, date: Date): Date {
    endDateTime.setFullYear(date.getFullYear());
    endDateTime.setMonth(date.getMonth());

    if (endDateTime.getHours() === 0 && endDateTime.getMinutes() === 0) {
        endDateTime.setDate(date.getDate() + 1);
    } else {
        endDateTime.setDate(date.getDate());
    }

    return endDateTime;
}

export function getStartDate(startDate: Date | undefined): Date {
    if (!startDate) {
        return new Date();
    }
    const tmpStartDate = new Date(startDate);
    const currentDay = new Date();

    if (tmpStartDate.getTime() > currentDay.getTime()) {
        return tmpStartDate;
    }

    return currentDay;
}
